import { useId } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PaletteUtils } from '@shared/utils';
import type { Attachments } from '@shared/api';
import { type FileType, MessageAttachment } from './MessageAttachment';
import { Button } from '@shared/widgets';
import ComposeIcon from '@assets/icons/messages/compose.svg';
import CloseIcon from '@assets/icons/messages/close.svg';
import AttachmentIcon from '@assets/icons/messages/attachmentRotate.svg';
import SendIcon from '@assets/icons/messages/send.svg';

export interface IMessageEditorForm {
  subject: string;
  body: string;
  attachments: (string | File)[] | null;
}
const DEFAULT_VALUES: IMessageEditorForm = { subject: '', body: '', attachments: null };

interface MessageEditorProps {
  threadId?: string;
  sending: boolean;
  error?: string | null;
  onClose: (value: boolean) => void;
  onSend: (data: IMessageEditorForm) => void;
}

export const MessageEditor = (props: MessageEditorProps) => {
  const id = useId();

  const messageEditorForm = useForm<IMessageEditorForm>({ defaultValues: DEFAULT_VALUES });
  const body = messageEditorForm.watch('body');
  const subject = messageEditorForm.watch('subject');
  const attachments = messageEditorForm.watch('attachments');

  const { t } = useTranslation('messages');

  const onSubmit = (data: IMessageEditorForm) => {
    props.onSend(data);
    props.onClose(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length) return;

    const newFiles = Array.from(e.target.files);
    const currentAttachments = messageEditorForm.getValues('attachments') || [];

    messageEditorForm.setValue('attachments', [...currentAttachments, ...newFiles]);

    e.target.value = '';
  };

  const handleRemoveAttachment = (file: File | FileType | Attachments) => {
    const currentAttachments = messageEditorForm.getValues('attachments') || [];
    const updatedAttachments = currentAttachments.filter((item) => item !== file);

    messageEditorForm.setValue('attachments', updatedAttachments);
  };

  const attachmentsElements =
    attachments &&
    attachments.map((item, idx) => (
      <MessageAttachment key={idx} className='bg-[#fcfcfc] px-6' file={item as File} onRemove={handleRemoveAttachment} />
    ));

  const canSend = body && (subject || props.threadId);
  return (
    <div
      className={clsx(
        'min-lg:bg-[#fcfcfc] min-lg:bottom-0 min-lg:h-auto min-lg:p-0 min-lg:absolute min-lg:right-0 min-lg:w-[500px] max-lg:bg-white max-lg:h-full max-lg:w-full',
        {
          'opacity-10': props.sending,
        },
      )}
    >
      <form noValidate onSubmit={messageEditorForm.handleSubmit(onSubmit)}>
        <div className='bg-transparent text-[#27272b] text-base leading-8 pt-6 pl-10 mb-7 h-[50px] flex items-center uppercase font-bold relative max-lg:bg-[#27272b] max-lg:text-white max-lg:leading-[50px] max-lg:pl-6 max-lg:pt-0 max-lg:mb-0'>
          <ComposeIcon className='mr-2' />
          {t(`modals.messageEditor.titles.${props.threadId ? 'reply' : 'newMessage'}`)}
          <CloseIcon
            className='pointer mt-0 absolute right-[30px] top-8 max-lg:-mt-[9px] max-lg:right-[14px] max-lg:top-1/2 cursor-pointer max-lg:text-white'
            onClick={() => props.onClose(false)}
          />
        </div>
        <div className='min-lg:min-h-[349px] max-h-[calc(100vh-300px)] pl-10 pr-15 overflow-x-hidden overflow-y-auto max-lg:max-h-[calc(100vh-122px)] max-lg:pl-0 max-lg:pr-0 max-lg:overflow-y-hidden'>
          {!props.threadId && (
            <Controller
              name='subject'
              control={messageEditorForm.control}
              rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('modals.messageEditor.placeholders.subject') }) }}
              render={({ field }) => (
                <div className='mb-5 h-[42px] relative max-lg:h-[57px] max-lg:mb-0'>
                  <textarea
                    className={clsx(
                      'focus:outline-none focus:ring-0 border-b  py-2 font-semibold bg-inherit text-[#27272b] block text-base h-full left-0 absolute top-0 w-full max-lg:py-[18px] max-lg:px-6 overflow-hidden',
                      {
                        'border-0 border-b-[#979797]/40': !subject,
                        'border-0 border-transparent max-lg:border-b-[#979797]/40': !!subject,
                      },
                    )}
                    placeholder={`${t('modals.messageEditor.placeholders.subject')} *`}
                    rows={1}
                    {...field}
                  />
                  <div className='font-inherit text-[16px] tracking-[-0.2px] leading-[1.38] break-words invisible whitespace-pre-wrap p-[18px_24px]'>
                    {field.value + '\n'}
                  </div>
                </div>
              )}
            />
          )}
          <Controller
            name='body'
            control={messageEditorForm.control}
            rules={{ required: t('errors.form.requiredField', { ns: 'common', field: t('modals.messageEditor.placeholders.body') }) }}
            render={({ field }) => (
              <div className='mb-5 h-[42px] relative max-lg:h-[57px] max-lg:mb-0'>
                <textarea
                  className={clsx(
                    'focus:outline-none focus:ring-0 border-b py-2 font-medium bg-inherit text-[#27272b] block text-base h-full left-0 absolute top-0 w-full max-lg:py-[18px] max-lg:px-6 overflow-hidden max-lg:border-b-0',
                    {
                      'border-0 border-b-[#979797]/40': !body,
                      'border-0 border-transparent max-lg:border max-md:': !!body,
                    },
                  )}
                  placeholder={`${t('modals.messageEditor.placeholders.enterMessage')} *`}
                  rows={1}
                  {...field}
                />
                <div className='font-inherit text-[16px] tracking-[-0.2px] leading-[1.38] break-words invisible whitespace-pre-wrap p-[18px_24px]'>
                  {field.value + '\n'}
                </div>
              </div>
            )}
          />
          {attachmentsElements && attachmentsElements.length > 0 && (
            <div className='bg-[#fcfcfc] py-5 px-0 -mr-5 max-lg:px-6'>{attachmentsElements}</div>
          )}
        </div>
        {props.error && <div className='text-[#d44112] text-base tracking-[-0.1px] mt-[9px] text-left px-[24px]'>{props.error}</div>}
        <div className='flex flex-row items-center justify-between p-4'>
          <input type='file' id={id} className='absolute invisible -z-10' multiple onChange={handleFileUpload} />
          <label htmlFor={id} className='text-[#27272b] text-[13px] font-semibold w-auto border-none cursor-pointer h-10 leading-10 uppercase flex'>
            <AttachmentIcon className={clsx('mr-2 relative top-[7px] ', PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#27272b]')} />{' '}
            {t('modals.messageEditor.buttons.attachFiles')}
          </label>
          <div className='group'>
            <Button
              className={clsx(
                'pl-10 pr-4.5 relative inline-block text-[13px] font-semibold !w-auto bg-transparent cursor-pointer h-10 py-3 text-center uppercase tracking-normal leading-3',
                !canSend &&
                  (PaletteUtils.isBordierPalette
                    ? '!text-[#6d6d75] !border-[#6d6d75] hover:bg-transparent'
                    : '!text-[#dadada] !border-[#dadada] hover:bg-transparent'),
                canSend && (PaletteUtils.isBordierPalette ? 'border-[#dc291c] hover:bg-[#dc291c] font-semibold' : '!text-[#6d6d75]'),
              )}
              caption={t('buttons.send', { ns: 'common' })}
              icon={
                <SendIcon
                  className={clsx(
                    ' h-5 w-5 absolute left-3 top-1/2 -mt-2.5 inline-block',
                    PaletteUtils.isBordierPalette ? 'text-[#dc291c]' : 'text-[#BE8B45]',
                    !canSend && (PaletteUtils.isBordierPalette ? '' : 'opacity-50'),
                    canSend && PaletteUtils.isBordierPalette && 'group-hover:text-white',
                  )}
                />
              }
              type='submit'
              disabled={!canSend}
            />
          </div>
        </div>
      </form>
      <div className='bg-black/20 h-full left-0 opacity-0 pointer-events-none absolute top-0 transition-opacity duration-300 w-full' />
    </div>
  );
};
